import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

import useAuthQuery from '@/hooks/auth/useAuthQuery';

import GlobalAlert from '@/components/GlobalAlert';
import Card from '@/components/Card/Card';
import FormInputComponent from '@/components/FormInputComponent';
import TextComponent from '@/components/TextComponent';
import VerificationForm from '@/components/VerificationForm';
import { validatePassword, validatePhone } from '@/utils/validate';
import ButtonComponent from '@/components/ButtonComponent';
import theme from '@/styles/theme';

interface ChangePasswordform {
  password: string;
  passwordCheck: string;
}

const FindAccountPage = () => {
  const location = useLocation<string>();
  const [activeTab, setActiveTab] = useState(location.state);
  const [phoneAuth] = useState(false);
  const [id, setId] = useState('');
  const [passwordToken, setPasswordToken] = useState('');
  const history = useHistory();

  const { findIdMutation, findPasswordMutation, changePasswordMutation } =
    useAuthQuery();

  const { control, handleSubmit, getValues, errors } = useForm<any>({
    defaultValues: {}
  });

  const onAuth = async (type: string, token: string, input: string) => {
    if (activeTab === 'id') {
      const { username } = await findIdMutation.mutateAsync({ token, input });
      setId(username);
    }
    if (activeTab === 'password') {
      const { username } = getValues();
      const { accessToken } = await findPasswordMutation.mutateAsync({
        token,
        input,
        username
      });
      setPasswordToken(accessToken);
    }
  };

  const changePassword = async (form: ChangePasswordform) => {
    const { password, passwordCheck } = form;
    if (password !== passwordCheck) {
      return GlobalAlert.show({
        title: '비밀번호를 확인해주세요.'
      });
    }

    const changed = await changePasswordMutation.mutateAsync({
      accessToken: passwordToken,
      newPassword: password
    });

    if (changed) {
      history.push('/');
      return GlobalAlert.show({
        title: '비밀번호가 변경되었습니다'
      });
    }
  };

  /** 아이디 찾기 */
  const findId = () => (
    <Content>
      {id ? (
        <ResultBox>
          <TextComponent fontSize={23} fontWeight={500}>
            회원님의 정보와 일치하는 계정입니다.
          </TextComponent>
          <ResultId>
            <TextComponent fontSize={30} fontWeight={700}>
              {id}
            </TextComponent>
          </ResultId>
        </ResultBox>
      ) : (
        <>
          <TextComponent
            fontSize={25}
            fontWeight={700}
            style={{ marginBottom: '4rem' }}>
            휴대폰 번호로 인증을 해주세요
          </TextComponent>
          <Controller
            as={
              <VerificationForm
                type={'phone'}
                authType="find_id"
                onAuth={onAuth}
                placeholder="휴대폰 번호 입력 (- 없이, 숫자만)"
                rules={{
                  required: '이름을 입력해주세요',
                  validate: validatePhone
                }}
                authSuccess={phoneAuth}
              />
            }
            defaultValue={''}
            noLabel
            control={control}
            name={'phone'}
          />
        </>
      )}
    </Content>
  );

  /** 비밀번호 찾기 */
  const findPassword = () => (
    <Content>
      {!passwordToken ? (
        <>
          <TextComponent
            fontSize={25}
            fontWeight={700}
            style={{ marginBottom: '3rem' }}>
            아이디, 휴대폰 번호로 인증을 해주세요
          </TextComponent>
          <FormInputComponent
            keyName="username"
            control={control}
            placeholder={'아이디 입력'}
            defaultValue={''}
            error={errors.username}
            rules={{
              minLength: { value: 5, message: '아이디를 확인해주세요' }
            }}
          />
          <Controller
            as={
              <VerificationForm
                type={'phone'}
                authType="find_id"
                onAuth={onAuth}
                placeholder="휴대폰 번호 입력 (- 없이, 숫자만)"
                rules={{
                  required: '이름을 입력해주세요',
                  validate: validatePhone
                }}
                authSuccess={phoneAuth}
              />
            }
            defaultValue={''}
            noLabel
            control={control}
            name={'phone'}
          />
        </>
      ) : (
        <ResultBox>
          <TextComponent
            fontSize={25}
            fontWeight={700}
            style={{ marginBottom: '4rem' }}>
            새로 변경하실 비밀번호를 입력해주세요
          </TextComponent>
          <FormInputComponent
            keyName="password"
            type="password"
            placeholder={'비밀번호를 입력해주세요. (최소 8자 이상)'}
            defaultValue={''}
            control={control}
            error={errors.password}
            rules={{
              required: '비밀번호는 최소 8자 이상 20자 이하 입력해주세요',
              validate: validatePassword
            }}
            maxLength={20}
          />
          <FormInputComponent
            keyName="passwordCheck"
            type="password"
            placeholder={'비밀번호를 한번 더 입력해주세요. (최소 8자 이상)'}
            defaultValue={''}
            control={control}
            error={errors.password}
            rules={{
              required: '비밀번호는 최소 8자 이상 20자 이하 입력해주세요',
              validate: validatePassword
            }}
            maxLength={20}
            style={{ marginBottom: '1rem' }}
          />
          <ButtonComponent
            bgColor={theme.DF_COLOR.GREEN500}
            borderColor={theme.DF_COLOR.GREEN500}
            textColor={theme.DF_COLOR.WHITE}
            fontWeight={600}
            fontSize={20}
            text={'비밀번호 변경'}
            onClick={handleSubmit(changePassword)}
          />
        </ResultBox>
      )}
    </Content>
  );

  return (
    <Wrapper>
      <LoginFormWrapper>
        <Card>
          <CardBody>
            <TypeTab>
              <TabText
                onClick={() => setActiveTab('id')}
                activeTab={activeTab === 'id'}>
                아이디 찾기
              </TabText>
              <TabText
                onClick={() => setActiveTab('password')}
                activeTab={activeTab === 'password'}>
                비밀번호 찾기
              </TabText>
            </TypeTab>
            {activeTab === 'id' ? findId() : findPassword()}
          </CardBody>
        </Card>
      </LoginFormWrapper>
    </Wrapper>
  );
};

export default FindAccountPage;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const LoginFormWrapper = styled.div`
  width: 55rem;
  margin-top: 3rem;
`;

const CardBody = styled.div`
  min-height: 72vh;
`;

const TypeTab = styled.div`
  display: flex;
  flex-flow: row;
  text-align: center;
  border-bottom: 1px solid ${theme.DF_COLOR.GRAY100};
  padding-top: 3rem;
  padding: 3rem 2rem 0;

  * {
    padding: 1rem 3rem;
    cursor: pointer;
  }
`;

const TabText = styled(TextComponent)<{ activeTab: boolean }>`
  font-size: 20px;
  font-weight: 700;
  ${({ activeTab }) => !activeTab && 'opacity: 0.3'};
  ${({ activeTab }) => activeTab && `border-bottom: 3px solid ${theme.DF_COLOR.PRIMARY500}}`};
`;

const Content = styled.div`
  padding: 7rem 6rem;
`;

const ResultBox = styled.div`
  text-align: center;
`;
const ResultId = styled.div`
  width: 100%;
  padding: 3rem 0 1rem;
  border-bottom: 1px solid ${theme.DF_COLOR.GRAY100};
`;
